/**
 * Sign-up wall experiment variant snapshot
 *
 * Module-level mirror of the `signup-incentive-wall` experiment arm
 * (`control` | `test`) for the current user, so pure helpers like
 * `enrichRedirectUrl` / `convertToRedirectUrl` can stamp `swv=<arm>` onto
 * redirect URLs — and therefore `link_clicks.metadata.swv` — without threading
 * the value through every result-card call site. This makes affiliate
 * commission (transactions ⨝ link_clicks) sliceable by experiment arm, the
 * off-PostHog revenue guardrail for the wall.
 *
 * Why a snapshot and not a prop or a fresh `useIsFeatureOn` at the click site:
 * the flag is evaluated in exactly ONE place — `SignupWallExperiment` in
 * `ResultsSignupWall.tsx` — which mounts only for experiment-eligible shoppers
 * (logged-out + desktop + results present). Reading the flag anywhere broader
 * (e.g. a result list rendered on mobile too) would fire `$feature_flag_called`
 * for ineligible users and contaminate the experiment's own exposures. The
 * snapshot lets that single gated evaluator publish its resolved arm and the
 * redirect builders read it, with zero extra flag evaluations.
 *
 * Stays `null` for everyone outside the eligible population, so their redirects
 * carry no `swv`.
 *
 * BROWSER-ONLY: `setSignupWallVariantSnapshot` must never be called
 * server-side. In Next.js, Node module singletons are shared across concurrent
 * SSR requests, so a server write would leak one user's arm onto every
 * in-flight response. The only caller is the client-only effect in
 * `SignupWallExperiment`.
 *
 * Timing note: like `extensionStateSnapshot`, the redirect builders read this
 * at render time. During SSR it is always `null` (the setter is client-effect
 * only), and a component that memoizes its `href` can compute a `swv`-less URL
 * until a client re-render recomputes the memo. This is a near-zero window —
 * clicks happen post-hydration, after the publish effect has run — but the next
 * reader should know the value is not guaranteed on the very first paint.
 *
 * Tests can drive it via `setSignupWallVariantSnapshot` and should call
 * `resetSignupWallVariantSnapshot` in `afterEach` to stay hermetic.
 */

let variant: string | null = null

export const setSignupWallVariantSnapshot = (next: string | null): void => {
  variant = next
}

export const getSignupWallVariantSnapshot = (): string | null => variant

/** Test helper — reset the module-level state between tests. */
export const resetSignupWallVariantSnapshot = (): void => {
  variant = null
}
